// Autosave session for one Markdown attachment (inventory F14/F15, ADR 0011 decision 7, docs/files-markdown.md).
// - 700 ms debounce after the last input, then PUT …/content with the last known `base_version`.
// - No Undo toast and no task edit per save (the server writes one `file_edited` activity per new version).
// - "Saved" indicator for 1500 ms after a successful save (reference timing).
// - Offline / server failure: the text stays in the editor and is written to the local draft store (IndexedDB),
//   then saved on `retry()` (called on reconnect / user retry). Never discarded silently.
// - 409 version_conflict: nothing is overwritten; both texts are kept and the user picks (no silent last-write-wins).
// The column and the large pop-up share one session (same draft), like the prototype's per-pane draft.
import { reactive } from 'vue';
import { TransportError, type Connectivity } from '@/app/ports';
import type { MarkdownApi, MdSaveResult, MdVersion } from './api';
import type { DraftStore } from './draftStore';

export const AUTOSAVE_MS = 700;
export const SAVED_FLASH_MS = 1500;

export type MdStatus = 'loading' | 'ready' | 'pending' | 'saving' | 'offline' | 'conflict' | 'error' | 'auth';

export interface MdConflict { local: string; server: string; serverVersion: number; serverBy: string; serverAt: string }

export interface MdSessionState {
  status: MdStatus;
  loaded: boolean;
  text: string;
  /** Last text known to be stored on the server (current version). */
  serverText: string;
  baseVersion: number;
  savedFlash: boolean;
  errorCode: string;
  conflict: MdConflict | null;
  /** True when the editor shows a locally kept draft that is not on the server yet. */
  hasLocalDraft: boolean;
  versions: MdVersion[] | null;
  versionsError: string;
  versionText: Record<number, string>;
}

export interface MdSessionDeps {
  api: MarkdownApi;
  store: DraftStore;
  fileId: string;
  connectivity?: Connectivity;
  now?: () => number;
  /** Called after every accepted save/restore (e.g. to refresh the file projection's size/revision). */
  onSaved?: (r: MdSaveResult) => void;
}

const errCode = (e: unknown): string => (e instanceof TransportError ? e.code || e.kind : 'error');

export class MarkdownSession {
  readonly state: MdSessionState = reactive({
    status: 'loading', loaded: false, text: '', serverText: '', baseVersion: 0, savedFlash: false, errorCode: '',
    conflict: null, hasLocalDraft: false, versions: null, versionsError: '', versionText: {},
  });

  private timer: ReturnType<typeof setTimeout> | null = null;
  private flashTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private again = false;
  private unsub: (() => void) | null = null;
  private disposed = false;

  constructor(private readonly deps: MdSessionDeps) {
    this.unsub = deps.connectivity?.onChange(online => { if (online) void this.retry(); }) ?? null;
  }

  get fileId(): string { return this.deps.fileId; }
  private now(): number { return (this.deps.now ?? Date.now)(); }

  /** Fetches the current content; restores a locally kept draft (saving it, or surfacing a conflict). */
  async load(): Promise<void> {
    const st = this.state;
    st.status = 'loading';
    const draft = await this.deps.store.get(this.deps.fileId).catch(() => undefined);
    try {
      const c = await this.deps.api.getContent(this.deps.fileId);
      st.serverText = c.content; st.baseVersion = c.version_no; st.loaded = true;
      if (!draft || draft.text === c.content) {
        if (draft) await this.deps.store.delete(this.deps.fileId).catch(() => undefined);
        st.text = c.content; st.hasLocalDraft = false; st.status = 'ready';
      } else if (draft.baseVersion === c.version_no) {
        st.text = draft.text; st.hasLocalDraft = true; st.status = 'pending';
        await this.save();
      } else {
        st.text = draft.text; st.hasLocalDraft = true;
        st.conflict = { local: draft.text, server: c.content, serverVersion: c.version_no, serverBy: c.updated_by, serverAt: c.updated_at };
        st.status = 'conflict';
      }
    } catch (e) {
      if (draft) { st.text = draft.text; st.baseVersion = draft.baseVersion; st.hasLocalDraft = true; st.loaded = true; }
      this.fail(e);
    }
  }

  /** Editor input: update immediately, autosave after 700 ms of inactivity. */
  input(v: string): void {
    const st = this.state;
    if (!st.loaded || this.disposed) return;
    st.text = v;
    if (this.timer) clearTimeout(this.timer);
    if (st.status === 'conflict') { void this.keepDraft(); return; } // resolve first; keep what is typed
    if (st.status === 'ready' || st.status === 'saving') st.status = 'pending';
    this.timer = setTimeout(() => { this.timer = null; void this.save(); }, AUTOSAVE_MS);
  }

  /** Saves now (cancels the debounce). Resolves when the server answered (or failed). */
  async flush(): Promise<void> {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    await this.save();
  }

  /** Reconnect / user retry. */
  async retry(): Promise<void> {
    const st = this.state;
    if (this.disposed) return;
    if (!st.loaded) { await this.load(); return; }
    if (st.status === 'offline' || st.status === 'error' || st.status === 'auth' || st.status === 'pending') await this.flush();
  }

  async save(): Promise<void> {
    if (this.inFlight) { this.again = true; return this.inFlight; }
    const st = this.state;
    if (!st.loaded || st.status === 'conflict') return;
    if (st.text === st.serverText && !st.hasLocalDraft) { if (st.status !== 'ready') st.status = 'ready'; return; }
    const snap = st.text;
    this.inFlight = (async () => {
      await this.keepDraft();
      st.status = 'saving';
      try {
        const r = await this.deps.api.putContent(this.deps.fileId, snap, st.baseVersion);
        this.accepted(r, snap);
      } catch (e) {
        await this.failSave(e, snap);
      }
    })();
    try { await this.inFlight; } finally { this.inFlight = null; }
    if (this.again) { this.again = false; if (!this.timer) await this.save(); }
  }

  /** Restores version `n` as a new version. Requires a connection; pending edits are saved first. */
  async restore(n: number): Promise<boolean> {
    const st = this.state;
    await this.flush();
    if (st.status !== 'ready') return false;
    try {
      const text = st.versionText[n] ?? (await this.loadVersionText(n));
      if (text === null) return false;
      st.status = 'saving';
      const r = await this.deps.api.restoreVersion(this.deps.fileId, n, st.baseVersion);
      st.text = text;
      this.accepted(r, text);
      await this.loadVersions();
      return true;
    } catch (e) {
      await this.failSave(e, st.text);
      return false;
    }
  }

  /** Conflict resolution: 'mine' saves the local text over the newer server version (theirs stays in history);
   *  'theirs' takes the server text and drops the local draft (explicit user choice). */
  async resolveConflict(choice: 'mine' | 'theirs'): Promise<void> {
    const st = this.state, c = st.conflict;
    if (!c) return;
    st.conflict = null;
    st.baseVersion = c.serverVersion; st.serverText = c.server;
    if (choice === 'theirs') {
      st.text = c.server; st.hasLocalDraft = false; st.status = 'ready';
      await this.deps.store.delete(this.deps.fileId).catch(() => undefined);
      return;
    }
    st.status = 'pending';
    await this.save();
  }

  /** Another device changed the file (sync delivered a new file revision): reload only when nothing local is pending. */
  async refreshRemote(): Promise<void> {
    const st = this.state;
    if (st.status !== 'ready' || st.text !== st.serverText || this.timer) return;
    try {
      const c = await this.deps.api.getContent(this.deps.fileId);
      if (st.status !== 'ready' || st.text !== st.serverText || this.timer) return; // user typed meanwhile
      st.text = c.content; st.serverText = c.content; st.baseVersion = c.version_no;
      if (st.versions) await this.loadVersions();
    } catch { /* stays on the current text; next save resolves through base_version */ }
  }

  async loadVersions(): Promise<void> {
    try { this.state.versions = await this.deps.api.listVersions(this.deps.fileId); this.state.versionsError = ''; }
    catch (e) { this.state.versionsError = errCode(e); }
  }

  async loadVersionText(n: number): Promise<string | null> {
    if (n in this.state.versionText) return this.state.versionText[n]!;
    try { const v = await this.deps.api.getVersion(this.deps.fileId, n); this.state.versionText[n] = v.content; return v.content; }
    catch (e) { this.state.versionsError = errCode(e); return null; }
  }

  dispose(): void {
    if (this.disposed) return;
    const pending = !!this.timer;
    if (this.timer) clearTimeout(this.timer);
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.timer = this.flashTimer = null;
    this.unsub?.(); this.unsub = null;
    if (pending || this.state.status === 'pending') void this.save().finally(() => { this.disposed = true; });
    else this.disposed = true;
  }

  // -----------------------------------------------------------------------------------------------------------
  private async keepDraft(): Promise<void> {
    const st = this.state;
    try { await this.deps.store.put({ fileId: this.deps.fileId, text: st.text, baseVersion: st.baseVersion, updatedAt: this.now() }); st.hasLocalDraft = true; }
    catch { /* quota / storage failure: the text is still in memory and is sent to the server */ }
  }

  private accepted(r: MdSaveResult, snap: string): void {
    const st = this.state;
    st.baseVersion = r.version_no; st.serverText = snap; st.errorCode = '';
    if (st.text === snap) {
      st.hasLocalDraft = false; st.status = 'ready';
      void this.deps.store.delete(this.deps.fileId).catch(() => undefined);
    } else st.status = 'pending';
    if (st.versions && r.result !== 'unchanged') void this.loadVersions();
    st.savedFlash = true;
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => { st.savedFlash = false; this.flashTimer = null; }, SAVED_FLASH_MS);
    this.deps.onSaved?.(r);
  }

  private async failSave(e: unknown, snap: string): Promise<void> {
    const st = this.state;
    if (e instanceof TransportError && e.code === 'version_conflict') {
      try {
        const c = await this.deps.api.getContent(this.deps.fileId);
        if (c.content === snap) { this.accepted({ ...c, result: 'unchanged', file: {} }, snap); return; }
        st.conflict = { local: st.text, server: c.content, serverVersion: c.version_no, serverBy: c.updated_by, serverAt: c.updated_at };
        st.status = 'conflict'; st.errorCode = 'version_conflict';
      } catch (e2) { this.fail(e2); }
      return;
    }
    this.fail(e);
  }

  private fail(e: unknown): void {
    const st = this.state;
    if (e instanceof TransportError && e.kind === 'network') { st.status = 'offline'; st.errorCode = 'network'; return; }
    if (e instanceof TransportError && e.kind === 'auth') { st.status = 'auth'; st.errorCode = 'auth'; return; }
    st.status = 'error'; st.errorCode = errCode(e);
  }
}

// One session per key (normally the file id) shared by the column and the pop-up.
const registry = new Map<string, { s: MarkdownSession; refs: number }>();

export function acquireMarkdownSession(key: string, make: () => MarkdownSession): MarkdownSession {
  const hit = registry.get(key);
  if (hit) { hit.refs++; return hit.s; }
  const s = make();
  registry.set(key, { s, refs: 1 });
  void s.load();
  return s;
}

export function releaseMarkdownSession(key: string): void {
  const hit = registry.get(key);
  if (!hit) return;
  if (--hit.refs <= 0) { registry.delete(key); hit.s.dispose(); }
}

/** For sync integration: the open session of a file, if any. */
export const markdownSessionFor = (key: string): MarkdownSession | undefined => registry.get(key)?.s;

/** Every open session (sync integration: retry offline drafts / reload idle files after a pull). */
export function forEachMarkdownSession(fn: (s: MarkdownSession, key: string) => void): void {
  for (const [k, v] of registry) fn(v.s, k);
}
